import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faDollarSign,
  faWrench,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import RepairTypeBadge from "./RepairTypeBadge";
import DamagePortionsBreakdown from "./DamagePortionsBreakdown";
import { interpolateD610ScoreFromSettings } from "../utils/d610Degradation";

/**
 * RepairCostDetailModal Component
 * Shows detailed breakdown of repair costs when a cost cell is clicked
 */
const RepairCostDetailModal = ({
  isOpen,
  onClose,
  repairData,
  d610Settings = {},
}) => {
  if (!isOpen || !repairData) return null;

  const isMobilization = repairData.costType === "mobilization";

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatArea = (area) => {
    return `${parseFloat(area || 0).toFixed(2)} m²`;
  };

  if (isMobilization) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-amber-700 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faDollarSign} className="text-2xl" />
                <div>
                  <h2 className="text-xl font-bold">
                    Mobilization Cost Details
                  </h2>
                  <p className="text-sm text-amber-100 mt-1">
                    Project-level mobilization for Year {repairData.year}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-amber-800 mb-1">
                Total Mobilization Cost
              </p>
              <p className="text-3xl font-bold text-amber-900">
                {formatCurrency(repairData.totalCost)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 border rounded p-3">
                <p className="text-gray-600">
                  Total Degraded Area / Painted Area
                </p>
                <p className="font-semibold text-gray-900">
                  {formatArea(repairData.totalArea || 0)} /{" "}
                  {formatArea(repairData.totalPaintedArea || 0)}
                </p>
              </div>
              <div className="bg-gray-50 border rounded p-3">
                <p className="text-gray-600">Repairs Triggered</p>
                <p className="font-semibold text-gray-900">
                  {repairData.repairsCount || 0} elements
                </p>
              </div>
              <div className="bg-gray-50 border rounded p-3">
                <p className="text-gray-600">Teams Required</p>
                <p className="font-semibold text-gray-900">
                  {repairData.teamsRequired || 0}
                </p>
              </div>
              <div className="bg-gray-50 border rounded p-3">
                <p className="text-gray-600">Weeks Required / Limit</p>
                <p className="font-semibold text-gray-900">
                  {(repairData.weeksRequired || 0).toFixed(2)} /{" "}
                  {repairData.weekLimit || 0}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-700">Team Labor Total</p>
                <p className="font-semibold text-blue-900">
                  {formatCurrency(repairData.teamLaborTotal)}
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-700">General Costs Total</p>
                <p className="font-semibold text-green-900">
                  {formatCurrency(repairData.generalCostsTotal)}
                </p>
              </div>
            </div>

            {repairData.configSummary && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-700">
                <p className="font-semibold text-gray-900 mb-2">
                  Configuration
                </p>
                <p>
                  {repairData.configSummary.teamName || "Team"} |{" "}
                  {repairData.configSummary.applicationType || "-"} |{" "}
                  {repairData.configSummary.speedBand || "-"} |{" "}
                  {repairData.configSummary.coatType || "-"} |{" "}
                  {repairData.configSummary.steelType || "-"}
                </p>
                {repairData.configSummary.teamComposition && (
                  <p className="mt-1 text-gray-600">
                    Team composition: {repairData.configSummary.teamComposition}
                  </p>
                )}
                {Number(repairData.paintSpeedSqmPerHour || 0) > 0 && (
                  <p className="mt-1 text-gray-600">
                    Weighted paint speed:{" "}
                    {Number(repairData.paintSpeedSqmPerHour).toFixed(2)} m²/hr
                  </p>
                )}
              </div>
            )}

            {(repairData.generalCostBreakdown || []).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  General Cost Breakdown
                </h3>
                <div className="space-y-1">
                  {(repairData.generalCostBreakdown || []).map((row) => (
                    <div
                      key={row.id || row.name}
                      className="flex items-center justify-between text-sm bg-white border rounded px-3 py-2"
                    >
                      <span className="text-gray-700">
                        {row.name || "General Cost"}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(row.convertedTotal)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 rounded-b-lg">
            <button
              onClick={onClose}
              className="w-full bg-amber-600 text-white py-2 px-4 rounded-md hover:bg-amber-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Use backend repair type/label as source of truth so table and modal match
  const getRepairType = () => {
    // Prefer repairLabel from new simulation engine (WASH, TU, OC, RE)
    const label = (repairData.repairLabel || "").toUpperCase();
    if (label === "WASH") return "wash";
    if (label === "TU") return "touchup";
    if (label === "OC") return "overcoat";
    if (label === "RE") return "recoat";

    // Then prefer repairType from backend (touchup, full)
    if (repairData.repairType) {
      const t = String(repairData.repairType).toLowerCase();
      if (t === "touchup" || t === "touch-up") return "touchup";
      if (t === "full") return "recoat";
    }

    // Fallback: infer from processes only when backend did not provide type
    if (
      !repairData.repairProcessesA &&
      !repairData.repairProcessesB &&
      !repairData.repairProcessesC
    ) {
      return repairData.repairType || "unknown";
    }

    const allProcesses = [
      ...(repairData.repairProcessesA || []),
      ...(repairData.repairProcessesB || []),
      ...(repairData.repairProcessesC || []),
    ].map((p) => (p.processName || p.name || "").toLowerCase());

    const hasWash = allProcesses.some((p) => p.includes("wash"));
    const hasPrime = allProcesses.some((p) => p.includes("prime"));
    const hasStripe = allProcesses.some((p) => p.includes("stripe"));
    const hasMid = allProcesses.some((p) => p.includes("mid"));
    const hasTop = allProcesses.some((p) => p.includes("top"));

    if (hasWash && !hasPrime && !hasStripe && !hasMid && !hasTop) return "wash";
    if (!hasPrime && !hasStripe && !hasMid && hasTop) return "touchup";
    if (hasPrime || hasStripe || hasMid) return "recoat";

    return repairData.repairType || "unknown";
  };

  const repairType = getRepairType();
  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === "string") {
      const normalized = value.replace(/,/g, "").trim();
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const toNumber = (value) => {
    const n = parseNumericValue(value);
    return Number.isFinite(n) ? n : 0;
  };
  // On a mixed element the row's corrosionCategory and degradationPercent are
  // the dominant portion's, not the portion that triggered this repair — a C5
  // portion firing at 0.52% is reported as "C3, 0.06%", which reads as a repair
  // below its own threshold. Label them as element-level and show the portions.
  const portionStates = Array.isArray(repairData.portionStates)
    ? repairData.portionStates
    : [];
  const isMixedElement = portionStates.length > 1;
  const triggeringPortions = Array.isArray(repairData.triggeringPortions)
    ? repairData.triggeringPortions
    : portionStates.filter((p) => p.triggered).map((p) => p.combinationCode);

  const hasDegradationPercent =
    repairData.degradationPercent !== undefined &&
    repairData.degradationPercent !== null;
  const degradationPercentValue = hasDegradationPercent
    ? parseNumericValue(repairData.degradationPercent)
    : null;
  let d610Score = null;

  if (hasDegradationPercent) {
    try {
      d610Score = interpolateD610ScoreFromSettings(
        degradationPercentValue,
        d610Settings,
      );
    } catch (error) {
      console.error("Error interpolating D610 score for repair detail modal:", error);
    }
  }

  const firstPositiveNumber = (...values) => {
    for (const value of values) {
      const numericValue = parseNumericValue(value);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
      }
    }
    return 0;
  };
  const springWashCost = Number(repairData.springWashCost || 0);
  const totalWashCost = Number(repairData.costWash || 0);
  const includedDefaultWashCost = Math.max(0, totalWashCost - springWashCost);
  const nonSpringElementRepairTotal = Math.max(
    0,
    Number(repairData.totalCost || 0) - springWashCost,
  );

  const details = repairData.calculationDetails || {};
  const breakdown = details.costBreakdown || repairData.costBreakdown || {};

  const directCostA = toNumber(
    repairData.costA ?? repairData.cost_a ?? breakdown.costA ?? breakdown.A,
  );
  const directCostB = toNumber(
    repairData.costB ?? repairData.cost_b ?? breakdown.costB ?? breakdown.B,
  );
  const directCostC = toNumber(
    repairData.costC ?? repairData.cost_c ?? breakdown.costC ?? breakdown.C,
  );

  const directAreaA = toNumber(repairData.areaA ?? repairData.area_a);
  const directAreaB = toNumber(repairData.areaB ?? repairData.area_b);
  const directAreaC = toNumber(repairData.areaC ?? repairData.area_c);

  const splitRaw =
    repairData.damageSplitUsed ||
    details.damageSplit ||
    details.damageSplitUsed;
  const splitA = toNumber(splitRaw?.A);
  const splitB = toNumber(splitRaw?.B);
  const splitC = toNumber(splitRaw?.C);
  const splitTotal = splitA + splitB + splitC;

  const paintOnlyTotal = Math.max(
    0,
    nonSpringElementRepairTotal - includedDefaultWashCost,
  );
  const totalDegradedArea = toNumber(
    repairData.degradedArea ?? repairData.degraded_area,
  );
  const selectedElementArea = firstPositiveNumber(
    repairData.totalArea,
    repairData.total_area,
    repairData.area,
    repairData.surfaceArea,
    details.totalArea,
  );
  const estimatedPaintedArea = firstPositiveNumber(
    repairData.repairArea,
    repairData.repair_area,
    repairData.estimatedRepairArea,
    repairData.estimated_repair_area,
    details.repairArea,
    repairData.areaA,
    repairData.area_a,
    0,
  );
  const estimatedPaintedAreaPercent = firstPositiveNumber(
    repairData.estimatedPaintedAreaPercent,
    repairData.estimated_painted_area_percent,
    details.estimatedPaintedAreaPercent,
    details.estimated_painted_area_percent,
  );

  const hasDirectSeverityCost =
    directCostA > 0 || directCostB > 0 || directCostC > 0;
  const canEstimateSeverity =
    !hasDirectSeverityCost && splitTotal > 0 && paintOnlyTotal > 0;

  const displayCostA = hasDirectSeverityCost
    ? directCostA
    : canEstimateSeverity
      ? (paintOnlyTotal * splitA) / splitTotal
      : 0;
  const displayCostB = hasDirectSeverityCost
    ? directCostB
    : canEstimateSeverity
      ? (paintOnlyTotal * splitB) / splitTotal
      : 0;
  const displayCostC = hasDirectSeverityCost
    ? directCostC
    : canEstimateSeverity
      ? (paintOnlyTotal * splitC) / splitTotal
      : 0;

  const hasDirectSeverityArea =
    directAreaA > 0 || directAreaB > 0 || directAreaC > 0;
  const canEstimateArea =
    !hasDirectSeverityArea && splitTotal > 0 && totalDegradedArea > 0;
  const displayAreaA = hasDirectSeverityArea
    ? directAreaA
    : canEstimateArea
      ? (totalDegradedArea * splitA) / splitTotal
      : 0;
  const displayAreaB = hasDirectSeverityArea
    ? directAreaB
    : canEstimateArea
      ? (totalDegradedArea * splitB) / splitTotal
      : 0;
  const displayAreaC = hasDirectSeverityArea
    ? directAreaC
    : canEstimateArea
      ? (totalDegradedArea * splitC) / splitTotal
      : 0;
  const estimatedPaintedAreaDisplay =
    estimatedPaintedArea > 0
      ? estimatedPaintedArea
      : displayAreaA + displayAreaB + displayAreaC;
  const selectedElementAreaDisplay =
    selectedElementArea > 0
      ? selectedElementArea
      : estimatedPaintedAreaPercent > 0 && estimatedPaintedAreaDisplay > 0
        ? estimatedPaintedAreaDisplay / (estimatedPaintedAreaPercent / 100)
        : 0;

  const showSeverityBreakdown =
    displayCostA > 0 || displayCostB > 0 || displayCostC > 0;

  // Canonical process order: Wash → Prime → Stripe → Midcoat → Topcoat (matches Repair Rules)
  const getCanonicalOrder = (name) => {
    const n = (name || "").toLowerCase();
    if (n.includes("wash")) return 1;
    if (n.includes("prime")) return 2;
    if (n.includes("stripe")) return 3;
    if (n.includes("mid")) return 4;
    if (n.includes("top")) return 5;
    return 999;
  };

  // Get all unique processes, ordered by sequence then by canonical order (so steps display correctly)
  const getAllProcesses = () => {
    const processes = new Map();

    [
      ...(repairData.repairProcessesA || []),
      ...(repairData.repairProcessesB || []),
      ...(repairData.repairProcessesC || []),
    ].forEach((p) => {
      const name = p.processName || p.name || "Unknown";
      if (!processes.has(name)) {
        processes.set(name, {
          name,
          sequenceOrder: p.sequenceOrder != null ? p.sequenceOrder : 999,
          canonicalOrder: getCanonicalOrder(name),
        });
      }
    });

    return Array.from(processes.values()).sort((a, b) => {
      if (a.sequenceOrder !== b.sequenceOrder)
        return a.sequenceOrder - b.sequenceOrder;
      return a.canonicalOrder - b.canonicalOrder;
    });
  };

  const processes = getAllProcesses();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faDollarSign} className="text-2xl" />
              <div>
                <h2 className="text-xl font-bold">Repair Cost Details</h2>
                <p className="text-sm text-blue-100 mt-1">
                  {repairData.spanCode ||
                    repairData.spanName ||
                    `Element ${repairData.spanId}`}{" "}
                  - Year {repairData.year}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* When user clicked a process row: lead with the amount they clicked so it matches the table */}
          {repairData.clickedCellCost != null &&
            repairData.clickedCellCost !== repairData.totalCost && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-800 mb-1">
                  This cell (from table)
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(repairData.clickedCellCost)}
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  {repairData.clickedRowName || "This row"} — component of the
                  element/year repair total. Element/year total:{" "}
                  {formatCurrency(repairData.totalCost)}
                </p>
              </div>
            )}

          {/* Repair Type Summary */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Repair Type</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <RepairTypeBadge repairType={repairType} />
                  <span className="text-lg font-semibold text-gray-900">
                    {repairType === "wash"
                      ? "Wash"
                      : repairType === "touchup"
                        ? "Touch-up"
                        : repairType === "overcoat"
                          ? "Overcoat"
                          : repairType === "recoat"
                            ? "Recoat/Full Repair"
                            : "Unknown"}
                  </span>
                  {(repairData.independentWash ||
                    repairData.repairLabel === "WASH") && (
                    <span className="text-xs text-blue-600 font-medium">
                      (Wash only — no paint job this year)
                    </span>
                  )}
                  {repairType !== "wash" && !repairData.independentWash && (
                    <span className="text-xs text-gray-500">
                      (Independent wash is not applied when a paint job is
                      scheduled)
                    </span>
                  )}
                </div>
              </div>
              {!(
                repairData.clickedCellCost != null &&
                repairData.clickedCellCost !== repairData.totalCost
              ) && (
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-1">Total Cost</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(repairData.totalCost)}
                  </p>
                </div>
              )}
              {repairData.clickedCellCost != null &&
                repairData.clickedCellCost !== repairData.totalCost && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600 mb-1">
                      Element/year total
                    </p>
                    <p className="text-xl font-semibold text-gray-700">
                      {formatCurrency(repairData.totalCost)}
                    </p>
                  </div>
                )}
            </div>
            {(springWashCost > 0 || includedDefaultWashCost > 0) && (
              <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-700 space-y-1">
                {includedDefaultWashCost > 0 && (
                  <p>
                    Included default wash (inside paint operation):{" "}
                    {formatCurrency(includedDefaultWashCost)}
                  </p>
                )}
                {springWashCost > 0 && (
                  <p>
                    Spring wash/rinse (separate):{" "}
                    {formatCurrency(springWashCost)}
                  </p>
                )}
                {repairType !== "wash" && (
                  <p>
                    Element/year non-spring repair total:{" "}
                    {formatCurrency(nonSpringElementRepairTotal)}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Processes Applied */}
          {processes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faWrench} />
                Processes Applied
              </h3>
              <div className="bg-white border border-gray-200 rounded p-3 text-sm text-gray-700">
                {processes
                  .map((process, idx) => `${idx + 1}-${process.name}`)
                  .join(", ")}
              </div>
            </div>
          )}

          {/* Cost Breakdown by Severity */}
          {showSeverityBreakdown && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faLayerGroup} />
                Cost Breakdown by Damage Severity
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {displayCostA > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 mb-1">
                      Severity A (Minor)
                    </p>
                    <p className="text-lg font-bold text-blue-900">
                      {formatCurrency(displayCostA)}
                    </p>
                    {displayAreaA > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        Area: {formatArea(displayAreaA)}
                      </p>
                    )}
                  </div>
                )}
                {displayCostB > 0 && (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-xs text-yellow-600 mb-1">
                      Severity B (Moderate)
                    </p>
                    <p className="text-lg font-bold text-yellow-900">
                      {formatCurrency(displayCostB)}
                    </p>
                    {displayAreaB > 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Area: {formatArea(displayAreaB)}
                      </p>
                    )}
                  </div>
                )}
                {displayCostC > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <p className="text-xs text-red-600 mb-1">
                      Severity C (Severe)
                    </p>
                    <p className="text-lg font-bold text-red-900">
                      {formatCurrency(displayCostC)}
                    </p>
                    {displayAreaC > 0 && (
                      <p className="text-xs text-red-600 mt-1">
                        Area: {formatArea(displayAreaC)}
                      </p>
                    )}
                  </div>
                )}
              </div>
              {!hasDirectSeverityCost && canEstimateSeverity && (
                <div className="mt-2 text-xs text-gray-500">
                  Severity costs estimated from damage split percentages for
                  this row.
                </div>
              )}
              <div className="mt-3 text-xs text-gray-600">
                Severity subtotal:{" "}
                {formatCurrency(
                  (displayCostA || 0) +
                    (displayCostB || 0) +
                    (displayCostC || 0),
                )}
                {includedDefaultWashCost > 0 && (
                  <span>
                    {" "}
                    + Included default wash:{" "}
                    {formatCurrency(includedDefaultWashCost)}
                  </span>
                )}
                {repairType !== "wash" && (
                  <span>
                    {" "}
                    = Non-spring repair total:{" "}
                    {formatCurrency(nonSpringElementRepairTotal)}
                  </span>
                )}
              </div>
            </div>
          )}

          <DamagePortionsBreakdown portions={portionStates} />

          {/* Additional Details */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Additional Information
            </h3>
            <div className="flex flex-wrap gap-2 text-sm">
              {isMixedElement && triggeringPortions.length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900">
                  Driven by: {triggeringPortions.join(", ")}
                </span>
              )}
              {repairData.corrosionCategory && (
                <span className="px-2.5 py-1 rounded-full bg-white border text-gray-800">
                  Corrosion{isMixedElement ? " (element)" : ""}:{" "}
                  {repairData.corrosionCategory}
                </span>
              )}
              {hasDegradationPercent && (
                <span className="px-2.5 py-1 rounded-full bg-white border text-gray-800">
                  Degradation{isMixedElement ? " (element)" : ""}:{" "}
                  {degradationPercentValue?.toFixed(2) || 0}%
                </span>
              )}
              {d610Score !== null && (
                <span className="px-2.5 py-1 rounded-full bg-white border text-gray-800">
                  {/* Derived from the element-level degradation above. */}
                  D610 Score{isMixedElement ? " (element)" : ""}:{" "}
                  {d610Score.toFixed(2)}
                </span>
              )}
              {(repairData.degradedArea !== undefined ||
                repairData.degraded_area !== undefined ||
                totalDegradedArea > 0) && (
                <span className="px-2.5 py-1 rounded-full bg-white border text-gray-800">
                  Damaged Area: {formatArea(totalDegradedArea)}
                </span>
              )}
              {selectedElementAreaDisplay > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-white border text-gray-800">
                  Selected Element Area:{" "}
                  {formatArea(selectedElementAreaDisplay)}
                </span>
              )}
              {estimatedPaintedAreaDisplay > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-white border text-gray-800">
                  Estimated Painted Area:{" "}
                  {formatArea(estimatedPaintedAreaDisplay)}
                </span>
              )}
              {repairData.yearsSinceRepair !== undefined && (
                <span className="px-2.5 py-1 rounded-full bg-white border text-gray-800">
                  Years Since Repair: {repairData.yearsSinceRepair || 0}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepairCostDetailModal;
